import { useCallback, useEffect, useState } from "react";
import { API, rupiah, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, CheckCircle } from "lucide-react";

const TABS = [
  { v: "kas", l: "Kas", hint: "Debit = kas masuk, Kredit = kas keluar" },
  { v: "piutang", l: "Piutang", hint: "Debit = piutang bertambah, Kredit = pelunasan" },
  { v: "hutang", l: "Hutang", hint: "Kredit = hutang bertambah, Debit = pembayaran" },
  { v: "modal", l: "Modal", hint: "Kredit = setoran modal, Debit = penarikan" },
];

export default function Accounting() {
  const [tab, setTab] = useState("kas");
  const [entries, setEntries] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    API.get("/accounting/entries", { params: { account: tab } }).then((r) => setEntries(r.data)).catch(() => setEntries([]));
    API.get("/accounting/saldo").then((r) => setSaldo(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (editId) await API.put(`/accounting/entries/${editId}`, payload);
      else await API.post("/accounting/entries", payload);
      toast.success("Entri disimpan");
      setForm(null);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus entri ini?")) return;
    try {
      await API.delete(`/accounting/entries/${id}`);
      toast.success("Entri dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const clearRecap = async () => {
    if (!window.confirm(`Hapus SELURUH rekap ${activeTab.l}? Semua entri pada akun ini akan dihapus permanen.`)) return;
    try {
      const { data } = await API.delete("/accounting/entries", { params: { account: tab } });
      toast.success(`Rekap ${activeTab.l} dihapus (${data.deleted} entri)`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const settle = async (id) => {
    if (!window.confirm("Tandai lunas seluruh sisa piutang/hutang ini?")) return;
    try {
      const { data } = await API.post(`/accounting/entries/${id}/settle`);
      toast.success(`Pelunasan ${rupiah(data.settled)} tercatat ke Kas`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";
  const activeTab = TABS.find((t) => t.v === tab);
  const canSettle = (e) => (tab === "piutang" || tab === "hutang") && (e.remaining ?? 0) > 0;

  return (
    <div className="space-y-6" data-testid="admin-accounting">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Kas, Piutang, Modal &amp; Hutang</h1>
          <p className="text-sm text-slate-500">Pencatatan otomatis dari pesanan &amp; transaksi + entri manual</p>
        </div>
        <button
          onClick={() => { setForm({ account: tab, date: new Date().toISOString().slice(0, 10), description: "", amount: "", direction: tab === "hutang" || tab === "modal" ? "kredit" : "debit" }); setEditId(null); }}
          data-testid="add-entry"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors"
        >
          <Plus className="h-4 w-4" /> Tambah Entri
        </button>
        <button
          onClick={clearRecap}
          data-testid="clear-recap"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-red-300 text-red-600 text-sm font-bold rounded-md hover:bg-red-50 transition-colors"
        >
          <Trash2 className="h-4 w-4" /> Hapus Rekap {activeTab.l}
        </button>
      </div>

      {saldo && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`saldo-${t.v}`} className={`text-left border rounded-md p-5 transition-colors ${tab === t.v ? "bg-[#0B1F3A] border-[#0B1F3A]" : "bg-white border-slate-200 hover:border-[#C9A227]"}`}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${tab === t.v ? "text-slate-300" : "text-slate-400"}`}>{t.l}</p>
              <p className={`text-lg font-bold ${tab === t.v ? "text-[#C9A227]" : "text-[#0B1F3A]"}`}>{rupiah(saldo[t.v])}</p>
            </button>
          ))}
      </div>
      )}

      <div className="flex gap-2 flex-wrap" data-testid="acct-tabs">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`acct-tab-${t.v}`} className={`px-5 py-2 text-xs font-bold rounded-full border transition-colors ${tab === t.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-500 hover:border-[#0B1F3A]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-400">{activeTab.hint}. Semua entri (termasuk berlabel "Otomatis") dapat ditambah, diubah, dan dihapus.</p>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="entries-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Tanggal</th>
              <th className="px-5 py-3">Keterangan</th>
              <th className="px-5 py-3 text-right">Debit</th>
              <th className="px-5 py-3 text-right">Kredit</th>
              {(tab === "piutang" || tab === "hutang") && <th className="px-5 py-3 text-right">Sisa</th>}
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map((e) => (
              <tr key={e.id} data-testid={`entry-row-${e.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-xs text-slate-500">{e.date}</td>
                <td className="px-5 py-3">
                  {e.description}
                  {e.source === "auto" && <span className="ml-2 text-[9px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">OTOMATIS</span>}
                </td>
                <td className="px-5 py-3 text-right font-semibold">{e.direction === "debit" ? rupiah(e.amount) : "-"}</td>
                <td className="px-5 py-3 text-right font-semibold">{e.direction === "kredit" ? rupiah(e.amount) : "-"}</td>
                {(tab === "piutang" || tab === "hutang") && (
                  <td className="px-5 py-3 text-right">
                    {e.remaining !== undefined ? (
                      <span className={`font-bold ${e.remaining > 0 ? "text-amber-600" : "text-emerald-700"}`}>{e.remaining > 0 ? rupiah(e.remaining) : "LUNAS"}</span>
                    ) : "-"}
                  </td>
                )}
                <td className="px-5 py-3 whitespace-nowrap text-right">
                  {canSettle(e) && (
                    <button onClick={() => settle(e.id)} data-testid={`settle-${e.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-md hover:bg-emerald-700 transition-colors mr-2">
                      <CheckCircle className="h-3.5 w-3.5" /> Lunasi
                    </button>
                  )}
                  <button onClick={() => { setForm({ account: e.account, date: e.date, description: e.description, amount: e.amount, direction: e.direction }); setEditId(e.id); }} data-testid={`edit-entry-${e.id}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(e.id)} data-testid={`delete-entry-${e.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Belum ada entri pada akun {activeTab.l}.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6" data-testid="entry-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Entri" : `Tambah Entri ${activeTab.l}`}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Akun</label>
                <select value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} data-testid="entry-account" className={inputCls}>
                  {TABS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Arah</label>
                <div className="grid grid-cols-2 gap-3">
                  {["debit", "kredit"].map((d) => (
                    <button key={d} onClick={() => setForm({ ...form, direction: d })} data-testid={`entry-${d}`} className={`p-3 rounded-md border text-sm font-bold capitalize transition-colors ${form.direction === d ? "border-[#C9A227] bg-amber-50 text-[#0B1F3A]" : "border-slate-300 text-slate-500"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Tanggal</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Keterangan *</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="entry-desc" className={inputCls} placeholder="Mis: Setoran modal awal / Beli stok dari supplier" />
              </div>
              <div>
                <label className={labelCls}>Jumlah (Rp) *</label>
                <input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="entry-amount" className={inputCls} />
              </div>
              <button onClick={save} disabled={!form.description || !form.amount} data-testid="save-entry" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
